import React from 'react';
import { useIntl } from 'react-intl';
import BaseLayout from 'layout/BaseLayout';
import {
  ChangelogContainer,
  List,
  ListItem,
  CategoryTitle,
  Title,
  VersionTitle,
  TypeTitle,
  TypeItem,
} from './ChangelogPage.style';

interface ChangeItem {
  category: string;
  items: string[];
}

interface ChangeLog {
  ajout: ChangeItem[];
  suppression: ChangeItem[];
  modification: ChangeItem[];
}

interface ChangelogData {
  version: string;
  changes: Partial<ChangeLog>;
}

const changelogData: ChangelogData[] = [
  {
    version: '4.0.0',
    changes: {
      ajout: [
        {
          category: 'Resilient',
          items: [
            'general-availability',
            'advanced-availability',
            'application-availability',
            'tls-certificate',
            'tls-certificate-renew',
            'infrastructure-drp-doc',
            'infra-deploy-documentation',
            'preprod-identique',
            'comptes-disctinct',
            'chaos-engeneering',
            'billing-alert',
            'no-manual-intervention',
            'no-manual-intervention-advanced',
          ],
        },
        {
          category: 'Operable',
          items: [
            'external-endpoints',
            'critical-endpoints-alert',
            'deployment-process',
            'third-party-doc',
            'iac-integration-tests',
            'iac-mapping-documentation',
            'infra-iac-gitflow',
          ],
        },
        {
          category: 'Secure',
          items: [
            'zero-credentials-ci-cid',
            'secure-ci-cd-secrets',
            'iam-minimum-rights-review',
            'private-network-solution',
            'protected-endpoints-waf',
            'network-filtering-matrix',
            'network-filtering-exit-point',
          ],
        },
        {
          category: 'Empowering',
          items: ['a-b-testing-feature', 'conventional-commits'],
        },
      ],
      suppression: [
        {
          category: 'Resilient',
          items: [
            'latence-basic',
            'latence-advanced',
            'monitor-kpi',
            'monitor-a-browser-test',
            'monitor-all-browser-tests',
            'sauvegardees-chiffrees',
            'chaos-engeneering-test',
            'infra-redeploy-documentation',
            'preprod-prod-identique',
            'comptes-separes',
            'chaos-engeneering-test',
            'deploy-with-maintained-versions',
            'non-aucune-version-majeure',
          ],
        },
        {
          category: 'Operable',
          items: [
            'critical-endpoints-alerting',
            'golden-metrics-dashboard',
            'architecture-schema',
            'components-list',
            'iac-security',
            'iac-versionning',
            'infra-iac',
          ],
        },
        {
          category: 'Secure',
          items: [
            'security-tools-vcs',
            'secure-ci-cd',
            'iam-minimum-rights',
            'attack-surface-minimum',
            'private-network-assets',
            'attack-surface-secured',
            'network-filtering',
            'unique-exit-point',
          ],
        },
        {
          category: 'Empowering',
          items: ['one-action-rollback', 'ci-enforce-gitflow'],
        },
      ],
      modification: [
        {
          category: 'Resilient',
          items: [
            'review-capacity',
            'automatiquement-adaptent',
            'utilise-service-manage',
          ],
        },
        {
          category: 'Operable',
          items: [
            'last-components-updates-date',
            'one-component-update-automatic',
            'all-components-update-automatic',
            'iac-in-ci',
          ],
        },
        {
          category: 'Secure',
          items: ['security-tools-application', 'secret-rotation'],
        },
      ],
    },
  },
  {
    version: '3.0.0',
    changes: {
      ajout: [
        {
          category: 'Secure',
          items: [
            'secure-vuln',
            'secure-git',
            'iam-lifecycle',
            'iam-sso',
            'iam-account-protection',
            'environment-isolated',
            'secret-rotation',
          ],
        },
      ],
      suppression: [
        {
          category: 'Secure',
          items: [
            'secure-deploy',
            'secure-output',
            'secure-vuln-tool',
            'infrastructure-mfa',
            'git-repository-mfa',
            'minimum-rights',
            'onboarding-offboarding-process',
            'no-privileges-elevation',
            'encrypted-critical-data',
          ],
        },
      ],
      modification: [
        {
          category: 'Secure',
          items: ['secure-tools', 'secure-intrusion'],
        },
      ],
    },
  },
  {
    version: '2.0.0',
    changes: {
      ajout: [
        {
          category: 'Operable',
          items: ['dry'],
        },
      ],
      modification: [
        {
          category: 'Operable',
          items: ['linting', 'standard', 'module'],
        },
      ],
    },
  },
];

const ChangelogPage: React.FC = () => {
  const intl = useIntl();
  return (
    <BaseLayout title="CHANGELOG">
      <ChangelogContainer>
        <Title>CHANGELOG</Title>
        {changelogData.map((log) => (
          <div key={log.version}>
            <VersionTitle>{log.version}</VersionTitle>
            <List>
              {Object.entries(log.changes).map(([key, value]) => (
                <TypeItem key={key}>
                  <TypeTitle>
                    {intl
                      .formatMessage({
                        id: 'changelog.' + key,
                      })
                      .toUpperCase()}
                  </TypeTitle>
                  {value.length > 0 && (
                    <List>
                      {value.map((subitem, index) => (
                        <TypeItem key={index}>
                          <CategoryTitle>- {subitem.category}</CategoryTitle>
                          <List>
                            {subitem.items.map((item, i) => (
                              <ListItem key={i}>
                                +{' '}
                                {intl.formatMessage({
                                  id: 'changelog.' + log.version + '.' + item,
                                })}{' '}
                              </ListItem>
                            ))}
                          </List>
                        </TypeItem>
                      ))}
                    </List>
                  )}
                </TypeItem>
              ))}
            </List>
          </div>
        ))}
      </ChangelogContainer>
    </BaseLayout>
  );
};

export default ChangelogPage;
